import BaseApiService from './BaseApiService.js';

class FeeService extends BaseApiService {

  // ✅ Create fee structure
  async createFee(feeData) {
    return this.makeRequest('/api/fees/create', {
      method: 'POST',
      body: JSON.stringify(feeData),
    });
  }

  // ✅ Get fee by student and organization
  async getFeeByStudentAndOrg(studentId, orgId) {
    if (!orgId) {
      console.warn('⚠️ OrgId is null or undefined, cannot fetch fees');
      return [];
    }
    // Try to convert to number if it's numeric, otherwise use as-is (for string codes like "SCH002")
    const finalOrgId = /^\d+$/.test(orgId) ? parseInt(orgId, 10) : orgId;
    return this.makeRequest(`/api/fees/student/${studentId}/org/${finalOrgId}`);
  }

  // ✅ Get all fees by organization (with optional academic year filter)
  async getFeesByOrg(orgId, academicYear) {
    if (!orgId) {
      console.warn('⚠️ OrgId is null or undefined, cannot fetch fees');
      return [];
    }
    // Try to convert to number if it's numeric, otherwise use as-is (for string codes like "SCH002")
    const finalOrgId = /^\d+$/.test(orgId) ? parseInt(orgId, 10) : orgId;
    const url = academicYear && academicYear !== 'all'
      ? `/api/fees/org/${finalOrgId}?academicYear=${encodeURIComponent(academicYear)}`
      : `/api/fees/org/${finalOrgId}`;
    return this.makeRequest(url);
  }

  // ✅ Get students with pending fees
  async getStudentsWithPendingFees(orgId) {
    if (!orgId) {
      console.warn('⚠️ OrgId is null or undefined, cannot fetch pending fees');
      return [];
    }
    // Try to convert to number if it's numeric, otherwise use as-is (for string codes like "SCH002")
    const finalOrgId = /^\d+$/.test(orgId) ? parseInt(orgId, 10) : orgId;
    return this.makeRequest(`/api/fees/pending/org/${finalOrgId}`);
  }

  // ✅ Process payment
  async processPayment(transactionData) {
    return this.makeRequest('/api/fees/payment', {
      method: 'POST',
      body: JSON.stringify(transactionData),
    });
  }

  // ✅ Get transaction history for student
  async getTransactionHistory(studentId, orgId) {
    if (!orgId) return [];
    // Try to convert to number if it's numeric, otherwise use as-is (for string codes like "SCH002")
    const finalOrgId = /^\d+$/.test(orgId) ? parseInt(orgId, 10) : orgId;
    return this.makeRequest(`/api/fees/transactions/student/${studentId}/org/${finalOrgId}`);
  }

  // ✅ Get all transaction history for organization
  async getOrgTransactionHistory(orgId) {
    if (!orgId) return [];
    const finalOrgId = /^\d+$/.test(orgId) ? parseInt(orgId, 10) : orgId;
    return this.makeRequest(`/api/fees/transactions/org/${finalOrgId}`);
  }

  // ✅ Fee type definitions
  getFeeTypes(institutionType = 'college') {
    if (institutionType === 'school') {
      return [
        { id: 'tuition', label: 'Tuition Fee', description: 'Regular tuition charges' },
        { id: 'vehicle', label: 'Vehicle Fee', description: 'Transportation/vehicle charges' },
        { id: 'school', label: 'School Fee', description: 'General school fees' },
        { id: 'activity', label: 'Activity Fee', description: 'Co-curricular activities' },
        { id: 'exam', label: 'Exam Fee', description: 'Examination charges' },
        { id: 'other', label: 'Other Fee', description: 'Miscellaneous charges' }
      ];
    } else {
      // College fee types
      return [
        { id: 'tuition', label: 'Tuition Fee', description: 'Regular tuition charges' },
        { id: 'vehicle', label: 'Vehicle Fee', description: 'Transportation/vehicle charges' },
        { id: 'college', label: 'College Fee', description: 'General college fees' },
        { id: 'lab', label: 'Lab Fee', description: 'Laboratory charges' },
        { id: 'library', label: 'Library Fee', description: 'Library charges' },
        { id: 'exam', label: 'Exam Fee', description: 'Examination charges' },
        { id: 'other', label: 'Other Fee', description: 'Miscellaneous charges' }
      ];
    }
  }

  // ✅ Utility methods for fee calculations
  calculatePendingFee(totalFees, paidFees) {
    return (totalFees || 0) - (paidFees || 0);
  }

  calculatePaymentProgress(totalFees, paidFees) {
    if (!totalFees || totalFees === 0) return 0;
    return ((paidFees || 0) / totalFees) * 100;
  }

  isFullyPaid(totalFees, paidFees) {
    return this.calculatePendingFee(totalFees, paidFees) === 0;
  }

  hasPendingAmount(totalFees, paidFees) {
    return this.calculatePendingFee(totalFees, paidFees) > 0;
  }

  getPaymentStatus(totalFees, paidFees) {
    const pendingAmount = this.calculatePendingFee(totalFees, paidFees);
    if (pendingAmount === 0) return 'PAID';
    if (pendingAmount > (totalFees * 0.5)) return 'OVERDUE';
    return 'PENDING';
  }
  // ✅ Get all fee structures
  async getFeeStructures() {
    return this.makeRequest('/api/fees/structures');
  }

  // ✅ Create fee structure template
  async createFeeStructure(structureData) {
    return this.makeRequest('/api/fees/structures/create', {
      method: 'POST',
      body: JSON.stringify(structureData),
    });
  }

  // ✅ Preview structure assignment
  async previewAssignFeeStructure(assignData) {
    return this.makeRequest('/api/fees/structures/assign/preview', {
      method: 'POST',
      body: JSON.stringify(assignData),
    });
  }

  // ✅ Execute structure assignment
  async assignFeeStructure(assignData) {
    return this.makeRequest('/api/fees/structures/assign', {
      method: 'POST',
      body: JSON.stringify(assignData),
    });
  }

  // ✅ Delete structure template
  async deleteFeeStructure(id) {
    return this.makeRequest(`/api/fees/structures/${id}`, {
      method: 'DELETE',
    });
  }

  // ✅ Apply auditable adjustment (concession, penalty, waiver)
  async applyAdjustment(adjustmentData) {
    return this.makeRequest('/api/fees/structures/adjust', {
      method: 'POST',
      body: JSON.stringify(adjustmentData),
    });
  }

  // ✅ Edit / Correct student fee with immutable audit trail
  async editFee(feeIdOrStudentId, editData) {
    const url = feeIdOrStudentId.startsWith('FEE_')
      ? `/api/fees/${feeIdOrStudentId}/correct`
      : `/api/fees/student/${feeIdOrStudentId}/correct`;
    return this.makeRequest(url, {
      method: 'PUT',
      body: JSON.stringify(editData),
    });
  }

  // ✅ Alias for correctFee
  async correctFee(feeIdOrStudentId, editData) {
    return this.editFee(feeIdOrStudentId, editData);
  }

  // ✅ Void an incorrectly entered fee without deleting historical records
  async voidFee(feeIdOrStudentId, reason = 'Fee entered by mistake') {
    const url = feeIdOrStudentId.startsWith('FEE_')
      ? `/api/fees/${feeIdOrStudentId}/void`
      : `/api/fees/student/${feeIdOrStudentId}/void`;
    return this.makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // ✅ Edit / Correct manual cash payment transaction amount (Admin Only)
  async editPaymentTransaction(transactionId, editData) {
    return this.makeRequest(`/api/fees/transactions/${transactionId}/edit`, {
      method: 'PUT',
      body: JSON.stringify(editData),
    });
  }

  // ✅ Get immutable edit history for a fee / student
  async getFeeHistory(feeIdOrStudentId, orgId) {
    if (feeIdOrStudentId && feeIdOrStudentId.startsWith('FEE_')) {
      return this.makeRequest(`/api/fees/${feeIdOrStudentId}/history`);
    }
    const finalOrgId = /^\d+$/.test(orgId) ? parseInt(orgId, 10) : orgId;
    return this.makeRequest(`/api/fees/history/${feeIdOrStudentId}/org/${finalOrgId || '1234'}`);
  }
  // ✅ Payment Settings API
  async getPaymentSettings() {
    return this.makeRequest('/api/payment/settings');
  }

  async savePaymentSettings(settingsData) {
    return this.makeRequest('/api/payment/settings', {
      method: 'POST',
      body: JSON.stringify(settingsData),
    });
  }

  // ✅ Send fee reminder alert notification to parents
  async sendReminderAlert(payload) {
    return this.makeRequest('/api/fees/send-reminder-alert', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

// ✅ Export single instance
const feeService = new FeeService();
export default feeService;

